import styles from './styles/GameOverview.module.scss';
import { useGameContainer } from '../index';
import LocalizedDate from 'components/LocalizedDate';
import { DEFAULT_SCHOOL_COVER_IMAGE } from 'constants.js.erb';
import dayjs from 'dayjs';
import cn from 'classnames';
import AccuWeatherImage from '../../../images/AccuWeather.png';
import Image from '../../../components/Image';
import { UPCOMING } from 'constants/GameStatuses';
import ContentSection, { ContentSectionFooterButton, ContentSectionHeader } from '@/components/ContentSection';
import AttributeRowImage from '@/components/AttributeRowImage';
import AttributeRow from '@/components/AttributeRow';
import AttributeRowFollowers from '@/components/AttributeRowFollowers';
import TextLink from '@/components/TextLink';
import ApplicationShareButton from 'containers/ApplicationContainer/components/ApplicationShareButton';

const ACCUWEATHER_DEFAULT_URL = 'https://www.accuweather.com/?partner=web_scorebook_adc';

export default function GameOverview() {
  const [{ game }] = useGameContainer();

  const mapQuery = game.latitude && game.longitude ? `${game.latitude}, ${game.longitude}` : null;
  const locationText = game.venue === game.locationText ? game.venue : `${game.venue} - ${game.locationText}`;

  const isUpcoming = game.statusId === UPCOMING;

  const date = dayjs(game.date);
  const isDay = date.hour() > 6 && date.hour() < 20;

  const isToday = date.date() === dayjs().date();

  let temperature: number | string | null;
  let iconNumber: string | undefined;
  let link: string | undefined;

  if (isUpcoming && game.weather_forecast_data && game.weather_forecast_data.maximum_temperature) {
    temperature =
      (parseInt(game.weather_forecast_data.maximum_temperature) + parseInt(game.weather_forecast_data.minimum_temperature || '0')) / 2;
    iconNumber = isDay ? game.weather_forecast_data.day_icon : game.weather_forecast_data.night_icon;
    link = game.weather_forecast_data.link;
  } else if ((!isUpcoming || isToday) && game.current_weather_conditions && game.current_weather_conditions.weather_text) {
    temperature = parseInt(game.current_weather_conditions.temperature || '');
    iconNumber = game.current_weather_conditions.weather_icon as string;
    link = game.current_weather_conditions.link as string;
  } else {
    temperature = null;
    link = undefined;
  }

  return (
    <ContentSection>
      <ContentSectionHeader right={<ApplicationShareButton />}>Game Information</ContentSectionHeader>
      <AttributeRowImage src={game.gameTeams[1].team.coverImage || DEFAULT_SCHOOL_COVER_IMAGE} alt={game.gameTeams[1].team.name} />
      {/* TODO FIX IN API */}
      {game.following_count! > 0 && <AttributeRowFollowers count={game.following_count!} title="Watchers" />}
      <AttributeRow title="Game Start">
        <LocalizedDate date={game.date} format="MMMM D - h:mm A" />
      </AttributeRow>
      <AttributeRow title="Sport">{game.genderSport.name}</AttributeRow>
      <AttributeRow title="Level">{game.level?.name}</AttributeRow>
      <AttributeRow title="Game Type">{game.gameTypeLabel}</AttributeRow>
      {game.contest?.bracket && (
        <AttributeRow title="Bracket">
          <TextLink href={game.contest.bracket.webPath} className={styles.BracketName}>
            <Image src={game.contest.bracket.logoImage} alt={game.contest.bracket.name} className={styles.BracketLogo} />
            <span className={styles.BracketName} title={game.contest.bracket.name}>
              {game.contest.bracket.name}
            </span>
          </TextLink>
        </AttributeRow>
      )}
      {game.contest?.bracketRound && <AttributeRow title="Bracket Round">{game.contest.bracketRound}</AttributeRow>}
      <AttributeRow title="Location">
        {mapQuery ? (
          <TextLink href={`http://www.google.com/maps/place/${mapQuery}`} target="_blank">
            {locationText}
          </TextLink>
        ) : (
          locationText
        )}
      </AttributeRow>
      {temperature && (
        <AttributeRow title="Weather">
          <TextLink className={styles.AccuWeatherInfo} href={link || ACCUWEATHER_DEFAULT_URL} target="_blank" rel="noopener">
            <span>
              {temperature}
              <span>&#176;</span>
            </span>
            <div className={cn(styles.WeatherIcon, `weather-icon-${iconNumber!}`)} />
          </TextLink>
        </AttributeRow>
      )}
      <ContentSectionFooterButton href={link || ACCUWEATHER_DEFAULT_URL}>
        See More On <img className={styles.AccuWeatherImage} src={AccuWeatherImage} />
      </ContentSectionFooterButton>
    </ContentSection>
  );
}
